import type { ScoreHistoryPoint } from "@/lib/score-history/query";

/** Unterhalb dieser Punktedifferenz gilt der Score als "stabil" statt gestiegen/gesunken -
 * der Score ist bereits eine 0-100-Ganzzahl, daher eine absolute statt einer relativen
 * Schwelle (anders als lib/trend-summary.ts::describeTrend, das mit Prozentwerten
 * verschiedenster Groessenordnung arbeitet). */
const STABLE_THRESHOLD_POINTS = 2;

/**
 * Kurzer, rein deskriptiver Ein-Satz-Trendhinweis fuer den Score-Verlauf - reine
 * Funktion, kein I/O, kein KI-Aufruf (gleiche Design-Entscheidung wie
 * lib/trend-summary.ts::describeTrend() und lib/scoring/scoring.ts::buildSummary():
 * ein deterministischer Satz ist bei einer simplen Start-zu-Ende-Aussage genauso
 * aussagekraeftig wie ein KI-Aufruf, aber sofort verfuegbar, kostenlos und ohne
 * Halluzinationsrisiko). Braucht mindestens 2 Datenpunkte - score_history wird erst seit
 * 2026-07-11 taeglich aufgezeichnet, in den ersten Tagen gibt es zwangslaeufig nur einen
 * einzigen Punkt je Ticker.
 */
export function describeScoreTrend(history: ScoreHistoryPoint[]): string | null {
  if (history.length < 2) return null;

  const sorted = [...history].sort((a, b) => a.capturedAt.localeCompare(b.capturedAt));
  const first = sorted[0];
  const last = sorted[sorted.length - 1];
  const days = Math.round(
    (new Date(last.capturedAt).getTime() - new Date(first.capturedAt).getTime()) / (24 * 3600 * 1000),
  );
  if (days < 1) return null;

  const period = days === 1 ? "einem Tag" : days < 14 ? `${days} Tagen` : `${Math.round(days / 7)} Wochen`;
  const delta = last.score - first.score;

  if (Math.abs(delta) < STABLE_THRESHOLD_POINTS) {
    return `Der Score blieb über ${period} weitgehend stabil bei rund ${last.score}.`;
  }

  const verb = delta > 0 ? "stieg" : "sank";
  return `Der Score ${verb} über ${period} von ${first.score} auf ${last.score} (${delta > 0 ? "+" : ""}${delta} Punkte).`;
}
